import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import LinearProgress from '@mui/material/LinearProgress'
import Typography from '@mui/material/Typography'
import DownloadIcon from '@mui/icons-material/Download'
import ErrorBanner from '../../components/shared/ErrorBanner'
import { shortLinkSummary } from '../../helpers/shortLinks'
import { generateShortLinks } from '../../services/shortLinkService'
import { toCSV } from '../../utils/csv'
import { downloadFile } from '../../utils/download'
import { STATUS, TEXT } from '../../theme/tokens'
import Overline from './Overline'

const SUMMARY_COLOR = {
    success: STATUS.success.fg,
    warning: STATUS.warning.fg,
    error: STATUS.error.fg,
}

/**
 * "Short links": tick the columns that hold links and replace every link
 * with a short bnz.one link before sending (the streaming route, with a
 * progress bar). The shortened rows become the rows that are sent; the
 * original rows stay as they were, so Try again starts from them.
 *
 * @param {object} props
 * @param {string[]} props.headers the file's columns
 * @param {object[]} props.rows the file's rows as parsed (never the shortened ones)
 * @param {object|null} props.result the short link result in use, or null
 * @param {(result: object|null) => void} props.onResult a new result, or
 *     null to send the original links
 * @param {(running: boolean) => void} props.onRunningChange
 * @param {string} props.downloadName file name for the processed file
 */
const ShortLinks = ({
    headers,
    rows,
    result,
    onResult,
    onRunningChange,
    downloadName,
}) => {
    const [selected, setSelected] = useState(result?.columns ?? [])
    const [running, setRunning] = useState(false)
    const [progress, setProgress] = useState(null)
    const [error, setError] = useState('')
    const mounted = useRef(true)

    useEffect(
        () => () => {
            mounted.current = false
        },
        []
    )

    const toggle = (column) =>
        setSelected((current) =>
            current.includes(column)
                ? current.filter((c) => c !== column)
                : headers.filter((h) => h === column || current.includes(h))
        )

    const run = async () => {
        setRunning(true)
        onRunningChange(true)
        setProgress(null)
        setError('')
        try {
            const made = await generateShortLinks({
                rows,
                headers,
                columns: selected,
                onProgress: (p) => mounted.current && setProgress(p),
            })
            if (mounted.current) onResult(made)
        } catch (err) {
            if (mounted.current)
                setError(err?.message || 'Short links were not made.')
        } finally {
            if (mounted.current) {
                setRunning(false)
                setProgress(null)
            }
            onRunningChange(false)
        }
    }

    const summary = result ? shortLinkSummary(result) : null
    const percent =
        progress?.total > 0
            ? Math.min(
                  100,
                  Math.round((progress.processed / progress.total) * 100)
              )
            : 0

    return (
        <Box sx={{ mb: 3 }} data-testid="short-links">
            <Overline sx={{ mb: 0.5 }}>Short links (optional)</Overline>
            <Typography variant="body2" sx={{ color: TEXT.faint, mb: 1 }}>
                Tick the columns that hold links. Each link is replaced with a
                short bnz.one link before sending.
            </Typography>
            <FormGroup row sx={{ mb: 1 }}>
                {headers.map((h) => (
                    <FormControlLabel
                        key={h}
                        control={
                            <Checkbox
                                size="small"
                                checked={selected.includes(h)}
                                onChange={() => toggle(h)}
                                disabled={running}
                            />
                        }
                        label={
                            <Typography
                                variant="body2"
                                sx={{ color: TEXT.secondary }}
                            >
                                {h}
                            </Typography>
                        }
                    />
                ))}
            </FormGroup>

            {running ? (
                <Box role="status" aria-live="polite">
                    <Typography
                        variant="body2"
                        sx={{ color: TEXT.muted, mb: 0.75 }}
                    >
                        {progress
                            ? `Making short links: ${progress.processed.toLocaleString()} of ${progress.total.toLocaleString()} rows`
                            : 'Uploading the rows…'}
                    </Typography>
                    <LinearProgress
                        variant={progress ? 'determinate' : 'indeterminate'}
                        value={percent}
                        aria-label="Short link progress"
                    />
                </Box>
            ) : (
                <Button
                    variant="outlined"
                    disabled={!selected.length || !rows.length}
                    onClick={run}
                >
                    Generate short links
                </Button>
            )}

            {error && !running && (
                <Box sx={{ mt: 1.5 }}>
                    <ErrorBanner message={error} />
                </Box>
            )}

            {summary && !running && (
                <Box sx={{ mt: 1.5 }}>
                    <Typography
                        variant="body2"
                        role="status"
                        sx={{ color: SUMMARY_COLOR[summary.level], mb: 1 }}
                    >
                        {summary.text}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {result.failedRows.length > 0 && (
                            <Button variant="contained" onClick={run}>
                                Try again
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() =>
                                downloadFile(
                                    toCSV(result.rows, headers),
                                    downloadName,
                                    'text/csv;charset=utf-8'
                                )
                            }
                        >
                            Download processed file
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => onResult(null)}
                        >
                            Send the original links
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    )
}

ShortLinks.propTypes = {
    headers: PropTypes.arrayOf(PropTypes.string).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    result: PropTypes.shape({
        rows: PropTypes.array,
        total: PropTypes.number,
        columns: PropTypes.arrayOf(PropTypes.string),
        failedRows: PropTypes.array,
    }),
    onResult: PropTypes.func.isRequired,
    onRunningChange: PropTypes.func.isRequired,
    downloadName: PropTypes.string.isRequired,
}

export default ShortLinks
